// src/api/recordings.ts - Recording related API calls

import { BASE_URL } from "../utils/config";
import { getAuthToken } from "../services/authService";
import {
	StartRecordingPayload,
	StartRecordingResponse,
	RecordingStatus,
} from "../types/entry";
import { RecordingReadyStatus } from "../types/api";

/**
 * Starts a recording for a hole entry.
 * Requires authentication.
 * @param holeSlug - The slug of the hole to record
 * @param entryId - The ID of the entry to record
 * @param hideFromTv - If true, do not show this shot on the clubhouse TV
 * @returns A promise resolving to the recording start response
 * @throws {Error} If the request fails or user is not authenticated
 */
export async function startRecording(
	holeSlug: string,
	entryId: number,
	hideFromTv: boolean = false,
): Promise<StartRecordingResponse> {
	const url = `${BASE_URL}/entries/start`;
	const token = getAuthToken();

	if (!token) {
		throw new Error("Authentication required to start recording.");
	}

	const payload: StartRecordingPayload = {
		hole_slug: holeSlug,
		entry_id: entryId,
		hide_from_tv: hideFromTv === true,
	};

	const body = JSON.stringify(payload);

	try {
		const response = await fetch(url, {
			method: "POST",
			headers: {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/json",
			},
			body,
		});

		if (!response.ok) {
			const errorData = await response.json().catch(() => ({}));
			throw new Error(
				errorData.error ||
					errorData.message ||
					`Failed to start recording: ${response.status}`,
			);
		}

		return (await response.json()) as StartRecordingResponse;
	} catch (error: any) {
		console.error("Error starting recording:", error);
		throw new Error(
			error.message || "An error occurred while starting recording.",
		);
	}
}

/**
 * Checks if a recording is ready/complete for a specific entry.
 * @param entryId - The ID of the entry to check
 * @returns A promise resolving to the recording status
 * @throws {Error} If the request fails
 */
export async function checkRecordingReady(
	entryId: number,
): Promise<RecordingReadyStatus> {
	const url = `${BASE_URL}/entries/${entryId}/ready`;

	try {
		const response = await fetch(url, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${getAuthToken()}`,
			},
		});

		if (!response.ok) {
			throw new Error(`Failed to check recording status: ${response.status}`);
		}

		return (await response.json()) as RecordingReadyStatus;
	} catch (error: any) {
		console.error("Error checking recording ready status:", error);
		throw new Error(
			error.message || "An error occurred while checking recording status.",
		);
	}
}

/**
 * Polls the recording status until it's ready or times out.
 * @param entryId - The ID of the entry to poll
 * @param onUpdate - Callback function called on each status update
 * @param maxAttempts - Maximum number of polling attempts (default: 60)
 * @param intervalMs - Polling interval in milliseconds (default: 2000)
 * @returns A promise resolving to true when ready, false if timed out
 */
export async function pollRecordingStatus(
	entryId: number,
	onUpdate?: (ready: boolean, attempt: number) => void,
	maxAttempts: number = 40,
	intervalMs: number = 5000,
): Promise<boolean> {
	for (let attempt = 1; attempt <= maxAttempts; attempt++) {
		try {
			const status = await checkRecordingReady(entryId);

			if (onUpdate) {
				onUpdate(status.ready, attempt);
			}

			if (status.ready) {
				return true;
			}

			// Wait before next poll
			await new Promise((resolve) => setTimeout(resolve, intervalMs));
		} catch (error) {
			console.error(`Polling attempt ${attempt} failed:`, error);
			// Continue polling even if one attempt fails
			await new Promise((resolve) => setTimeout(resolve, intervalMs));
		}
	}

	return false; // Timed out
}

/**
 * Circle overlay data from LIDAR-based terrain analysis
 */
export interface CircleOverlayData {
	version: number;
	original_size: {
		width: number;
		height: number;
	};
	center: {
		x: number;
		y: number;
	};
	circles: Array<{
		radius_m: number;
		label: string;
		points: Array<[number, number]>;
	}>;
}

/**
 * Fetches the circle overlay data for a given share slug if available.
 * The circles represent 1m and 2m distance rings around the hole,
 * pre-calculated from LIDAR data accounting for terrain.
 * @param shareSlug - The share slug identifier for the recording
 * @returns A promise resolving to the circle overlay data, or null if not available
 */
export async function getCircleOverlay(
	shareSlug: string,
): Promise<CircleOverlayData | null> {
	const url = `${BASE_URL}/recordings/circles/${shareSlug}`;

	try {
		const response = await fetch(url, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
			},
		});

		if (!response.ok) {
			return null;
		}

		const data = await response.json();
		return data as CircleOverlayData;
	} catch (error) {
		console.error("Error fetching circle overlay data:", error);
		return null;
	}
}
